// Booking Contracts Management Permissions

export const BOOKING_CONTRACT_VIEW = 259;
export const BOOKING_CONTRACT_CREATE = 260;
export const BOOKING_CONTRACT_UPDATE = 261;
export const BOOKING_CONTRACT_CONFIRM = 262;
// The total is a price preview, gated by the permission to view a booking.
export const BOOKING_CONTRACT_CALCULATE = BOOKING_CONTRACT_VIEW;

/**
 * Publishing a draft (reserve its slots, price it, issue its serial) and deleting a draft.
 *
 * Registered by amer-laravel's
 * `2026_08_27_140004_add_booking_contract_publish_and_delete_permissions.php`, which takes
 * each row id from the central key service's approval number. **These are the ids the
 * service actually issued**, read off the live `permissions` table — not the 1501/1502 that
 * following on from `SERVICE_CONTRACTS_PUBLISH` (1500) would have suggested. The service
 * does not hand out consecutive ids per PR, so a predicted id is a guess to verify, never a
 * value to rely on.
 */
export const BOOKING_CONTRACT_PUBLISH = 1512;
export const BOOKING_CONTRACT_DELETE = 1513;

/**
 * Cancelling a published contract (releases its slots and keeps the record).
 *
 * **Aliased to `BOOKING_CONTRACT_VIEW` deliberately, and this is the one gate in the module
 * that is looser than its endpoint.** `booking_contract.cancel` was registered by
 * `2026_07_23_000007_add_booking_contract_cancel_permission.php`, whose row id came from
 * the central key service — it is not in `PermissionsTableSeeder.php` (which stops at the
 * 259-263 block above) and nothing in either repo records it, so there is no id here to
 * write down. Guessing one is worse than aliasing: an id that happens to belong to another
 * permission would show or hide the button on an unrelated grant.
 *
 * Nothing is escalated by this — the route still carries
 * `permission:booking_contract.cancel`, so a user without it is refused by the API. The
 * only cost is a visible button that can fail. Replace this with the real id once it can be
 * read off a live tenant's permissions table; this is the single place to change.
 */
export const BOOKING_CONTRACT_CANCEL = BOOKING_CONTRACT_VIEW;

/**
 * Printing / downloading the confirmation voucher, and re-sending it to the guest.
 *
 * Registered by
 * `2026_09_02_140002_add_booking_contract_print_and_send_confirmation_permissions.php`,
 * which takes each row id from the central key service's approval number. **These are the
 * ids the service issued** — 1517 and 1518, not the 1514/1515 that following on from
 * `BOOKING_CONTRACT_PUBLISH`/`_DELETE` (1512/1513) would have suggested, which is the
 * second time in this module that a predicted id would have been wrong.
 */
export const BOOKING_CONTRACT_PRINT = 1517;
export const BOOKING_CONTRACT_SEND_CONFIRMATION = 1518;

/**
 * The front desk's two writes: the guest arrived, and the guest left.
 *
 * Registered by
 * `2026_09_02_150002_add_booking_contract_check_in_and_check_out_permissions.php`, which
 * takes each row id from the central key service's approval number. **These are the ids
 * the service issued** — 1519 and 1520, read off the approval rather than derived. They do
 * follow 1517/1518 consecutively, which is the first time in this module that the obvious
 * prediction would have been right: the two before it were not (1512/1513 not 1501/1502,
 * 1517/1518 not 1514/1515). One coincidence is not a rule — read the id, never derive it.
 */
export const BOOKING_CONTRACT_CHECK_IN = 1519;
export const BOOKING_CONTRACT_CHECK_OUT = 1520;
